import * as React from "react";
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";

import { Suppression, useReleaseSuppression } from "@/hooks/api/use-suppressions";

/**
 * Lifting a suppression puts someone back on a dialler after they asked not to be there, so the
 * reason is required rather than optional. It is the one field an auditor will ask for, and a
 * free-form confirm dialog would never have captured it.
 */
export function ReleaseSuppressionDialog({
	suppression,
	onClose,
	onReleased,
}: {
	suppression: Suppression | null;
	onClose: () => void;
	onReleased: () => void;
}): React.JSX.Element {
	const [note, setNote] = React.useState("");
	const releaseMutation = useReleaseSuppression();

	React.useEffect(() => {
		if (suppression) setNote("");
	}, [suppression]);

	const handleClose = () => {
		if (releaseMutation.isPending) return;
		onClose();
	};

	const handleRelease = async () => {
		if (!suppression || note.trim().length === 0) return;
		await releaseMutation.mutateAsync({ id: suppression.id, note: note.trim() });
		onClose();
		onReleased();
	};

	return (
		<Dialog
			open={Boolean(suppression)}
			onClose={releaseMutation.isPending ? undefined : handleClose}
			sx={{
				"& .MuiDialog-container": {
					"& .MuiPaper-root": {
						width: "100%",
					},
				},
			}}
		>
			<DialogTitle>
				Remove from do-not-call list
				<IconButton color="inherit" onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
					<XIcon width={20} height={20} />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ py: 4!, pt: "8px !important" }}>
				<Stack spacing={2.5}>
					<Alert severity="warning">
						<Typography variant="body2">
							{suppression?.phone ?? "This contact"} will become callable by your campaigns again. Only do this if the
							person has asked to be contacted, or the entry was a mistake.
						</Typography>
					</Alert>

					{suppression?.note ? (
						<Stack spacing={0.5}>
							<Typography variant="caption" color="text.secondary">
								Why they were added
							</Typography>
							<Typography variant="body2">{suppression.note}</Typography>
						</Stack>
					) : null}

					<TextField
						label="Reason for removing"
						value={note}
						onChange={(event) => setNote(event.target.value)}
						placeholder="e.g. Customer called back and asked to be contacted again on 31 July"
						helperText="Required. Recorded against this entry along with your name and the time, and kept permanently."
						multiline
						minRows={2}
						required
						fullWidth
						autoFocus
					/>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ borderTop: "1px solid var(--mui-palette-divider)", py: 2, px: 3 }}>
				<Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
					<Button color="secondary" onClick={handleClose} disabled={releaseMutation.isPending}>
						Cancel
					</Button>
					<Button
						onClick={handleRelease}
						disabled={note.trim().length === 0 || releaseMutation.isPending}
						variant="contained"
						color="error"
					>
						Remove from list
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
}
